import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2 } from "lucide-react";

const LESSON_TYPES = ["reading", "checklist", "worksheet", "quick_check", "case_study", "red_flag", "resource"];
const STAGES = ["Foundations", "Find and Approve", "Build and Launch", "Run and Grow"];

export default function Admin() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [quizzes, setQuizzes] = useState([]);

  const loadRefs = useCallback(() => {
    api.get("/admin/modules").then((r) => setModules(r.data)).catch(() => {});
    api.get("/admin/quizzes").then((r) => setQuizzes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const moduleOptions = modules.map((m) => ({ value: m.id, label: `M${m.module_number} · ${m.title}` }));
  const quizOptions = quizzes.map((q) => ({ value: q.id, label: q.title }));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Admin</h1>
            <p className="text-slate-600 mt-1">Edit imported course content, quizzes, upsells and compliance copy.</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/dashboard")}>View as student</Button>
        </div>

        <Tabs defaultValue="students">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="students" data-testid="admin-tab-students">Students</TabsTrigger>
            <TabsTrigger value="modules" data-testid="admin-tab-modules">Modules</TabsTrigger>
            <TabsTrigger value="lessons" data-testid="admin-tab-lessons">Lessons</TabsTrigger>
            <TabsTrigger value="checklist" data-testid="admin-tab-checklist">Checklists</TabsTrigger>
            <TabsTrigger value="quizzes" data-testid="admin-tab-quizzes">Quizzes</TabsTrigger>
            <TabsTrigger value="questions" data-testid="admin-tab-questions">Questions</TabsTrigger>
            <TabsTrigger value="upsells" data-testid="admin-tab-upsells">Upsells</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="admin-tab-compliance">Compliance</TabsTrigger>
          </TabsList>

          <TabsContent value="students" className="mt-5">
            <StudentsTab />
          </TabsContent>

          <TabsContent value="modules" className="mt-5">
            <Resource
              name="module" endpoint="/admin/modules" onChange={loadRefs}
              columns={[
                { key: "module_number", label: "#", render: (r) => r.module_number },
                { key: "title", label: "Title" },
                { key: "stage", label: "Stage" },
                { key: "is_published", label: "Status", render: (r) => <Badge className={r.is_published ? "bg-emerald-100 text-emerald-800 border-0" : "bg-slate-200 text-slate-600 border-0"}>{r.is_published ? "Published" : "Draft"}</Badge> },
              ]}
              publishToggle
              fields={[
                { key: "module_number", label: "Module number", type: "number" },
                { key: "title", label: "Title", type: "text" },
                { key: "stage", label: "Stage", type: "select", options: STAGES.map((s) => ({ value: s, label: s })) },
                { key: "summary", label: "Summary", type: "textarea" },
                { key: "learning_objectives", label: "Learning objectives (one per line)", type: "list" },
                { key: "key_terms", label: "Key terms (comma separated)", type: "csv" },
                { key: "action_step", label: "Action step", type: "textarea" },
                { key: "warning_note", label: "Warning note", type: "textarea" },
                { key: "order_index", label: "Order index", type: "number" },
                { key: "is_published", label: "Published", type: "boolean" },
              ]}
              defaults={{ module_number: modules.length + 1, stage: STAGES[0], is_published: true, order_index: modules.length + 1, learning_objectives: [], key_terms: [] }}
            />
          </TabsContent>

          <TabsContent value="lessons" className="mt-5">
            <Resource
              name="lesson" endpoint="/admin/lessons" filterKey="module_id" filterOptions={moduleOptions} filterLabel="Module"
              columns={[
                { key: "lesson_number", label: "#", render: (r) => r.lesson_number },
                { key: "title", label: "Title" },
                { key: "lesson_type", label: "Type" },
                { key: "is_required", label: "Required", render: (r) => (r.is_required ? "Yes" : "No") },
              ]}
              fields={[
                { key: "module_id", label: "Module", type: "select", options: moduleOptions },
                { key: "lesson_number", label: "Lesson number", type: "number" },
                { key: "title", label: "Title", type: "text" },
                { key: "lesson_type", label: "Type", type: "select", options: LESSON_TYPES.map((t) => ({ value: t, label: t })) },
                { key: "body_content", label: "Body content", type: "textarea", big: true },
                { key: "estimated_minutes", label: "Estimated minutes", type: "number" },
                { key: "is_required", label: "Required", type: "boolean" },
                { key: "worksheet_fields", label: "Worksheet fields (one per line; worksheet lessons only)", type: "list" },
                { key: "order_index", label: "Order index", type: "number" },
              ]}
              defaults={{ lesson_type: "reading", lesson_number: 1, estimated_minutes: 5, is_required: false, order_index: 1 }}
            />
          </TabsContent>

          <TabsContent value="checklist" className="mt-5">
            <Resource
              name="checklist item" endpoint="/admin/checklist-items" filterKey="module_id" filterOptions={moduleOptions} filterLabel="Module"
              columns={[
                { key: "title", label: "Title" },
                { key: "required", label: "Required", render: (r) => (r.required ? "Yes" : "No") },
              ]}
              fields={[
                { key: "module_id", label: "Module", type: "select", options: moduleOptions },
                { key: "title", label: "Title", type: "text" },
                { key: "required", label: "Required", type: "boolean" },
                { key: "warning_if_skipped", label: "Warning if skipped", type: "textarea" },
                { key: "order_index", label: "Order index", type: "number" },
              ]}
              defaults={{ required: false, order_index: 1 }}
            />
          </TabsContent>

          <TabsContent value="quizzes" className="mt-5">
            <Resource
              name="quiz" endpoint="/admin/quizzes" onChange={loadRefs} noDelete
              columns={[
                { key: "title", label: "Title" },
                { key: "passing_score", label: "Pass %" },
                { key: "is_published", label: "Published", render: (r) => (r.is_published ? "Yes" : "No") },
              ]}
              fields={[
                { key: "module_id", label: "Module", type: "select", options: moduleOptions },
                { key: "title", label: "Title", type: "text" },
                { key: "passing_score", label: "Passing score", type: "number" },
                { key: "is_required_for_completion", label: "Required for completion", type: "boolean" },
                { key: "is_published", label: "Published", type: "boolean" },
              ]}
              defaults={{ passing_score: 60, is_required_for_completion: true, is_published: true }}
            />
          </TabsContent>

          <TabsContent value="questions" className="mt-5">
            <Resource
              name="question" endpoint="/admin/quiz-questions" filterKey="quiz_id" filterOptions={quizOptions} filterLabel="Quiz"
              columns={[
                { key: "question_text", label: "Question" },
                { key: "question_type", label: "Type" },
                { key: "correct_answer", label: "Correct" },
              ]}
              fields={[
                { key: "quiz_id", label: "Quiz", type: "select", options: quizOptions },
                { key: "question_text", label: "Question", type: "textarea" },
                { key: "question_type", label: "Type", type: "select", options: [{ value: "multiple_choice", label: "Multiple choice" }, { value: "true_false", label: "True / False" }] },
                { key: "options", label: "Options (one per line)", type: "list" },
                { key: "correct_answer", label: "Correct answer (exact option text)", type: "text" },
                { key: "explanation", label: "Explanation", type: "textarea" },
                { key: "order_index", label: "Order index", type: "number" },
              ]}
              defaults={{ question_type: "multiple_choice", options: [], order_index: 1 }}
            />
          </TabsContent>

          <TabsContent value="upsells" className="mt-5">
            <Resource
              name="upsell" endpoint="/admin/upsells"
              columns={[
                { key: "title", label: "Title" },
                { key: "placement_type", label: "Placement" },
                { key: "price_text", label: "Price" },
                { key: "is_active", label: "Active", render: (r) => (r.is_active ? "Yes" : "No") },
              ]}
              fields={[
                { key: "title", label: "Title", type: "text" },
                { key: "description", label: "Description", type: "textarea" },
                { key: "price_text", label: "Price text", type: "text" },
                { key: "cta_text", label: "CTA text", type: "text" },
                { key: "cta_url", label: "CTA URL", type: "text" },
                { key: "placement_type", label: "Placement", type: "select", options: [{ value: "dashboard", label: "Dashboard" }, { value: "module_reminder", label: "Module reminder" }] },
                { key: "trigger_module", label: "Trigger module number (for reminders)", type: "number" },
                { key: "is_active", label: "Active", type: "boolean" },
                { key: "order_index", label: "Order index", type: "number" },
              ]}
              defaults={{ placement_type: "dashboard", is_active: true, order_index: 1 }}
            />
          </TabsContent>

          <TabsContent value="compliance" className="mt-5">
            <Resource
              name="compliance block" endpoint="/admin/compliance" noDelete
              columns={[
                { key: "title", label: "Title" },
                { key: "display_location", label: "Location" },
              ]}
              fields={[
                { key: "page_key", label: "Page key", type: "text" },
                { key: "title", label: "Title", type: "text" },
                { key: "body", label: "Body", type: "textarea", big: true },
                { key: "display_location", label: "Location", type: "select", options: ["checkout", "dashboard", "footer", "onboarding"].map((v) => ({ value: v, label: v })) },
              ]}
              defaults={{ display_location: "dashboard" }}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StudentsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/students").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");

  const openDetail = async (uid) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/students/${uid}`);
      setDetail(data);
    } catch {
      toast.error("Could not load student");
    }
    setDetailLoading(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-900" /></div>;
  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">{rows.length} student{rows.length === 1 ? "" : "s"} · click a row for detail</p>
      {rows.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">No students yet.</div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Name", "Email", "Sign-in", "Access", "Progress", "Quizzes", "Last login", "Joined"].map((h) => (
                  <th key={h} className="text-left font-semibold text-slate-600 px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((s) => (
                <tr key={s.user_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(s.user_id)} data-testid={`student-row-${s.user_id}`}>
                  <td className="px-4 py-3 text-slate-800 font-medium">{s.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{s.email}</td>
                  <td className="px-4 py-3 text-slate-500 capitalize">{s.auth_provider}</td>
                  <td className="px-4 py-3">
                    {s.has_access
                      ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Active</Badge>
                      : <Badge className="bg-slate-200 text-slate-600 border-0">None</Badge>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.modules_completed}/{s.total_modules} modules</td>
                  <td className="px-4 py-3 text-slate-600">{s.quiz_attempts}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(s.last_login_at)}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="student-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.user?.name || "Student"}</DialogTitle></DialogHeader>
          {detailLoading || !detail ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-900" /></div>
          ) : (
            <div className="space-y-5 py-2">
              <p className="text-sm text-slate-500">{detail.user.email} · <span className="capitalize">{detail.user.auth_provider}</span> · {detail.user.has_access ? "Active access" : "No access"}</p>

              <div>
                <h3 className="font-display font-semibold text-slate-900 mb-2 text-sm">Modules</h3>
                <div className="space-y-1">
                  {detail.modules.map((m) => (
                    <div key={m.module_number} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                      <span className="flex items-center gap-2">
                        {m.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="w-4 h-4 rounded-full border border-slate-300 inline-block" />}
                        <span className="text-slate-700">M{m.module_number}: {m.title}</span>
                      </span>
                      <span className="text-slate-400 text-xs whitespace-nowrap">{m.lessons_done}/{m.lessons_total} lessons{m.skipped_required?.length ? ` · ${m.skipped_required.length} skipped` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-display font-semibold text-slate-900 mb-2 text-sm">Quiz attempts</h3>
                {detail.quizzes.length === 0 ? <p className="text-sm text-slate-400">No quiz attempts yet.</p> : (
                  <div className="space-y-1">
                    {detail.quizzes.map((q, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                        <span className="text-slate-700">{(q.module_id || "").replace("mod-", "Module ")}</span>
                        <span className="text-slate-500">{q.score}% ({q.correct}/{q.total}) · {new Date(q.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-display font-semibold text-slate-900 mb-2 text-sm">Worksheets filled</h3>
                {detail.worksheets.length === 0 ? <p className="text-sm text-slate-400">No worksheets filled yet.</p> : (
                  <div className="space-y-1">
                    {detail.worksheets.map((w, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                        <span className="text-slate-700">{w.lesson_title}</span>
                        <span className="text-slate-400 text-xs">{w.fields_filled} field{w.fields_filled === 1 ? "" : "s"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Resource({ name, endpoint, columns, fields, defaults = {}, filterKey, filterOptions = [], filterLabel, publishToggle, noDelete, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = filterKey && filter ? { [filterKey]: filter } : {};
    api.get(endpoint, { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [endpoint, filterKey, filter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const base = { ...defaults };
    if (filterKey && filter) base[filterKey] = filter;
    setForm(base);
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item);
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.key];
      if (f.type === "number") v = v === "" || v === undefined || v === null ? null : Number(v);
      if (f.type === "boolean") v = !!v;
      if ((f.type === "list" || f.type === "csv") && typeof v === "string") {
        v = f.type === "csv" ? v.split(",").map((x) => x.trim()).filter(Boolean) : v.split("\n").map((x) => x.trim()).filter(Boolean);
      }
      payload[f.key] = v === undefined ? (f.type === "list" || f.type === "csv" ? [] : "") : v;
    });
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(`Saved ${name}`);
      setDialogOpen(false);
      load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail ? JSON.stringify(e.response.data.detail) : "Could not save");
    }
    setSaving(false);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${name}?`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success("Deleted");
      load();
      onChange && onChange();
    } catch {
      toast.error("Could not delete");
    }
  };

  const togglePublish = async (item) => {
    try {
      await api.patch(`${endpoint}/${item.id}/publish`);
      load();
      onChange && onChange();
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        {filterKey ? (
          <div className="w-72">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid={`admin-filter-${name.replace(/\s/g, "-")}`}><SelectValue placeholder={`Filter by ${filterLabel}`} /></SelectTrigger>
              <SelectContent>
                {filterOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ) : <div />}
        <Button className="bg-slate-900 hover:bg-slate-800" onClick={openNew} data-testid={`admin-${name.replace(/\s/g, "-")}-create-button`}>
          <Plus className="w-4 h-4 mr-1.5" /> New {name}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-900" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">{filterKey && !filter ? `Select a ${filterLabel} to view items.` : `No ${name}s yet.`}</div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {columns.map((c) => <th key={c.key} className="text-left font-semibold text-slate-600 px-4 py-3">{c.label}</th>)}
                <th className="px-4 py-3 w-32" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50" data-testid={`admin-row-${item.id}`}>
                  {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-700 max-w-xs truncate">{c.render ? c.render(item) : item[c.key]}</td>)}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {publishToggle && (
                        <Button variant="ghost" size="icon" onClick={() => togglePublish(item)} title="Toggle publish" data-testid={`admin-publish-toggle-${item.id}`}>
                          {item.is_published ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`admin-edit-${item.id}`}><Pencil className="w-4 h-4" /></Button>
                      {!noDelete && <Button variant="ghost" size="icon" onClick={() => remove(item)} data-testid={`admin-delete-${item.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${name}` : `New ${name}`}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <Field key={f.key} field={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving} data-testid="admin-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ field, value, onChange }) {
  const common = { "data-testid": `admin-field-${field.key}` };
  if (field.type === "boolean") {
    return (
      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
        <Label>{field.label}</Label>
        <Switch checked={!!value} onCheckedChange={onChange} {...common} />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div className="space-y-1.5">
        <Label>{field.label}</Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger {...common}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="space-y-1.5">
        <Label>{field.label}</Label>
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={field.big ? 8 : 3} {...common} />
      </div>
    );
  }
  if (field.type === "list" || field.type === "csv") {
    const str = Array.isArray(value) ? value.join(field.type === "csv" ? ", " : "\n") : value || "";
    return (
      <div className="space-y-1.5">
        <Label>{field.label}</Label>
        <Textarea value={str} onChange={(e) => onChange(e.target.value)} rows={4} {...common} />
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label>{field.label}</Label>
      <Input type={field.type === "number" ? "number" : "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...common} />
    </div>
  );
}
